import { useEffect, useRef, useState } from "react";

/* ── Hex grid constants ──────────────────────────────────────────── */
const S       = 38;
const COLS    = 18;
const ROWS    = 13;
const CW      = S * 1.5;
const RH      = S * Math.sqrt(3);
const W       = COLS * CW + S;
const H       = ROWS * RH + S;
const HOVER_R = 115;

const HEXES = (() => {
  const list: { id: string; cx: number; cy: number }[] = [];
  for (let c = 0; c < COLS; c++) {
    for (let r = 0; r < ROWS; r++) {
      const cx = c * CW + S;
      const cy = r * RH + (c % 2 ? RH / 2 : 0) + RH / 2;
      list.push({ id: `${c},${r}`, cx, cy });
    }
  }
  return list;
})();

function hexPts(cx: number, cy: number) {
  return Array.from({ length: 6 }, (_, i) => {
    const a = (Math.PI / 3) * i;
    return `${(cx + (S - 2) * Math.cos(a)).toFixed(1)},${(cy + (S - 2) * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
}

function HexGrid() {
  const svgRef = useRef<SVGSVGElement>(null);
  const [mouse, setMouse] = useState<{ x: number; y: number } | null>(null);

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      const svg = svgRef.current;
      if (!svg) return;
      const b = svg.getBoundingClientRect();
      const scaleX = W / b.width;
      const scaleY = H / b.height;
      const x = (e.clientX - b.left) * scaleX;
      const y = (e.clientY - b.top)  * scaleY;
      setMouse(x >= 0 && x <= W && y >= 0 && y <= H ? { x, y } : null);
    };
    document.addEventListener("mousemove", onMove);
    return () => document.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <svg
      ref={svgRef}
      viewBox={`0 0 ${W} ${H}`}
      className="kc-hex-background"
      style={{
        position: "fixed",
        top: 0,
        right: 0,
        pointerEvents: "none",
        userSelect: "none",
        zIndex: 0,
        height: "100vh",
        width: `${((W / H) * 100).toFixed(2)}vh`,
      }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="hexFadeKC" x1={W} y1="0" x2="0" y2={H} gradientUnits="userSpaceOnUse">
          <stop offset="0%"   stopColor="white" stopOpacity="1" />
          <stop offset="38%"  stopColor="white" stopOpacity="0.65" />
          <stop offset="65%"  stopColor="white" stopOpacity="0.1" />
          <stop offset="80%"  stopColor="white" stopOpacity="0" />
        </linearGradient>
        <mask id="hexMaskKC">
          <rect width={W} height={H} fill="url(#hexFadeKC)" />
        </mask>
      </defs>
      <g mask="url(#hexMaskKC)">
        {HEXES.map(({ id, cx, cy }) => {
          const d = mouse ? Math.hypot(cx - mouse.x, cy - mouse.y) : Infinity;
          const t = d < HOVER_R ? (1 - d / HOVER_R) ** 2 : 0;
          return (
            <polygon
              key={id}
              points={hexPts(cx, cy)}
              fill={`rgba(253,224,71,${(t * 0.15).toFixed(4)})`}
              stroke="rgba(253,224,71,0.10)"
              strokeWidth={1}
            />
          );
        })}
      </g>
    </svg>
  );
}

export function SvgBackground() {
  return <HexGrid />;
}
